import type { FC } from 'react';
import React from 'react';
import { IconButton, Stack, styled } from '@mui/material';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { Iconify } from '@pflegenavi/web-components';
import { NavigationBarImagePreview } from './NavigationBarImagePreview';
import * as Sentry from '@sentry/react';

interface ImagesPreviewNavigationBarProps {
  imageUrls: string[];
  imageUrlToTypeMap: UrlTypeNameMapping;
  selectedImageUrl: string;
  onSelectPrevious: () => void;
  onSelectNext: () => void;
  onSelectDifferentImage: (imageUrl: string) => void;
  imageUrlsToDisplay: string[];
}

export const ImagesPreviewNavigationBar: FC<
  ImagesPreviewNavigationBarProps
> = ({
  selectedImageUrl,
  onSelectDifferentImage,
  onSelectPrevious,
  onSelectNext,
  imageUrlsToDisplay,
  imageUrls,
  imageUrlToTypeMap,
}) => {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      {imageUrls.length > 4 && (
        <NavigationButton
          direction={'back'}
          onClick={onSelectPrevious}
          disabled={imageUrls[0] === imageUrlsToDisplay[0]}
        />
      )}
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        sx={{
          width: '100%',
        }}
      >
        {}
        {imageUrlsToDisplay.map((imageUrl) => {
          const file = imageUrlToTypeMap.get(imageUrl);

          const isLoading = file === undefined;
          const isPdf = file?.type === 'application/pdf';
          const isImage = file?.type?.startsWith('image/') ?? false;

          if (!isLoading && !isPdf && !isImage) {
            // eslint-disable-next-line no-console
            console.warn(
              `File with content-type ${file.type} is not supported: ${imageUrl}`
            );
            Sentry.captureMessage(
              `File with content-type ${file.type} is not supported: ${imageUrl}`,
              'warning'
            );
          }

          return (
            <NavigationBarImagePreview
              onImageSelect={onSelectDifferentImage}
              imageUrl={imageUrl}
              fileName={file?.name ?? 'unknown'}
              isLoading={isLoading}
              isPdf={isPdf}
              isImage={isImage}
              key={imageUrl}
              selected={selectedImageUrl === imageUrl}
            />
          );
        })}
      </Stack>
      {imageUrls.length > 4 && (
        <NavigationButton
          direction={'forward'}
          onClick={onSelectNext}
          disabled={
            imageUrls[imageUrls.length - 1] ===
            imageUrlsToDisplay[imageUrlsToDisplay.length - 1]
          }
        />
      )}
    </Stack>
  );
};

interface NavigationButtonProps {
  direction: 'back' | 'forward';
  onClick: () => void;
  disabled: boolean;
}

export const NavigationButton: FC<NavigationButtonProps> = ({
  direction,
  onClick,
  disabled,
}) => {
  return (
    <Stack>
      <NavigationBarButton onClick={onClick} disabled={disabled}>
        <Iconify icon={`eva:arrow-ios-${direction}-fill`} />
      </NavigationBarButton>
    </Stack>
  );
};

const NavigationBarButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    padding: 0,
    width: 20,
    borderRadius: 6,
    bgcolor: 'white',
    '&:hover': {
      bgcolor: 'white',
      opacity: 0.7,
    },
  })
);
